import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Plus, Globe, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useSites } from '@/hooks/use-sites';
import { useMe } from '@/hooks/use-me';
import { EmptyState } from '@/components/common/empty-state';
import { SiteCardSkeleton } from '@/components/common/loading-skeleton';

export const Route = createFileRoute('/_authenticated/sites/')({
  component: SitesList,
});

function SitesList() {
  const { data, isLoading } = useSites();
  const { data: me } = useMe();
  const navigate = useNavigate();
  const isAdmin = me?.member.role === 'admin';

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-8">
        <header className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="mb-1 text-2xl font-semibold tracking-tight">Sites</h1>
            <p className="text-sm text-text-muted">All tracked landing pages and websites.</p>
          </div>
        </header>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, i) => <SiteCardSkeleton key={i} />)}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-6 py-8 animate-fade-up">
      <header className="mb-8 flex items-start justify-between gap-6">
        <div>
          <h1 className="mb-1 text-2xl font-semibold tracking-tight">Sites</h1>
          <p className="text-sm text-text-muted">
            {data?.sites.length ?? 0} sites · All preview environment
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => navigate({ to: '/sites/new' })}>
            <Plus className="size-3.5" /> Add site
          </Button>
        )}
      </header>

      {data?.sites.length === 0 ? (
        <EmptyState
          title="No sites yet"
          description="Add your first landing page or website to start tracking user behavior."
          ctaLabel={isAdmin ? 'Add your first site' : undefined}
          onCta={isAdmin ? () => navigate({ to: '/sites/new' }) : undefined}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {data?.sites.map((site) => (
            <Link
              key={site.id}
              to="/sites/$siteId"
              params={{ siteId: site.id }}
              className="group"
            >
              <Card className="p-5 transition-colors hover:border-border-strong">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="min-w-0">
                    <h3 className="font-semibold truncate">{site.name}</h3>
                    <p className="text-xs text-text-muted font-mono truncate flex items-center gap-1.5 mt-0.5">
                      <Globe className="size-3" /> {site.domain}
                    </p>
                  </div>
                  <ExternalLink className="size-3.5 text-text-subtle opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                </div>
                <div className="flex items-baseline gap-4 pt-3 border-t border-border">
                  <div>
                    <div className="text-xl font-mono font-medium">{site.stats.sessions_24h}</div>
                    <div className="text-[10px] uppercase tracking-wider text-text-muted">24h</div>
                  </div>
                  <div>
                    <div className="text-xl font-mono font-medium">{site.stats.sessions_7d}</div>
                    <div className="text-[10px] uppercase tracking-wider text-text-muted">7d</div>
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
